import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { Menu } from '@headlessui/react';
import {
  Content,
  type DropdownMenuContentProps,
  Item,
  Root,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { type ReactNode, useState } from 'react';

export type OptionsItem = {
  key: string;
  name: string;
  display?: ReactNode;
  disabled?: boolean;
  isSelected?: boolean;
};

export type DropdownMenuSimpleProps = {
  buttonClassName?: ClassName;
  children: ReactNode;
  items: Array<OptionsItem>;
  onItemSelect: (clickedItem: { key: string; name: string; value: boolean }) => void;
  isOpen?: boolean;
} & Partial<Pick<DropdownMenuContentProps, 'align' | 'side'>>;

export function DropdownMenuSimple({
  children,
  items,
  onItemSelect,
  buttonClassName,
  isOpen,
  side,
  align = 'start',
}: DropdownMenuSimpleProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpen);
  return (
    <Menu>
      <Root modal={false} open={isMenuOpen} onOpenChange={(e) => setIsMenuOpen(e)}>
        <Trigger className={twx(buttonClassName)} aria-label="dropdown">
          {children}
        </Trigger>
        <Content
          align={align}
          side={side}
          // Prevent focus from closing menu, this fixes
          // an issue with interactive testing where a "click"
          // would fire twice, once for the mousedown and
          // again for focus on the button being clicked.
          onFocusOutside={(event) => event.preventDefault()}
          className={tw`z-menu mt-1 min-w-[15rem] rounded-md border border-divider-main bg-white py-1 shadow ring-background-subtle`}
          collisionPadding={10}
        >
          {items.map((menuItem) => (
            <Item
              key={menuItem.key}
              className={twx(
                'flex w-full cursor-pointer whitespace-pre-wrap px-2 py-1 text-background-inverse text-sm capitalize outline-none hover:bg-background-hover',
              )}
              onClick={() => {
                if (!menuItem.disabled) {
                  onItemSelect({
                    key: menuItem.key,
                    name: menuItem.name,
                    value: !menuItem.isSelected,
                  });
                }
              }}
            >
              <div
                className={twx('flex w-full justify-between', {
                  'text-content-icon': menuItem.disabled,
                  '!text-background-inverse': !menuItem.disabled,
                })}
              >
                {menuItem.display ? menuItem.display : menuItem.name}
              </div>
            </Item>
          ))}
        </Content>
      </Root>
    </Menu>
  );
}
